import type { UserId } from "@tento-lona/sheets";
import type { Option } from "@tento-lona/sheets";
import type { LedgerBumper } from "@tento-lona/sheets/internal-sdk";
import type { ClientLifecycle } from "../client/handle-errors.ts";
import type { PreferencesStorage } from "./preferences-storage.ts";
import type { SheetHost } from "../client/sheet-host.ts";
import { Actor, type InvalidationSink } from "../invalidation.ts";
import { CalendarPreferences } from "./calendar-preferences.ts";
import { SheetViewPreferences } from "./sheet-view-preferences.ts";
import { CalendarVisibilityPreferences } from "./calendar-visibility-preferences.ts";
import { SidebarWidthPreferences } from "./sidebar-width-preferences.ts";
import { RowHeightPreferences } from "./row-height-preferences.ts";
import { TransitionPreferences } from "./transition-preferences.ts";
import { TimezonePreferences } from "./timezone-preferences.ts";
import { DockedColumnCountPreferences } from "./docked-column-count-preferences.ts";
import type { SheetId } from "@tento-lona/sheets";

/**
 * The user's preferences (`client.preferences`): calendar, timezone,
 * sheet-view, visibility, row-height, sidebar, docked-column and transition
 * settings, stored locally. Each face reads with `peek()` (a list face:
 * `peekAll()`) and writes with `set(…, actor)`, announcing the change
 * stamped with the writer. The theme is `client.themes`.
 */
export class PreferencesClient {
  static readonly ACTOR = new Actor("preferences-client");
  readonly #storage: PreferencesStorage;
  readonly #host: SheetHost;
  #calendarPrefs: Option<CalendarPreferences> = null;
  #sidebarWidthPrefs: Option<SidebarWidthPreferences> = null;
  #rowHeightPrefs: Option<RowHeightPreferences> = null;
  #transitionsPrefs: Option<TransitionPreferences> = null;
  #timezonePrefs: Option<TimezonePreferences> = null;
  #dockedColumnCountPrefs: Option<DockedColumnCountPreferences> = null;

  readonly #invalidation: InvalidationSink;
  /**
   * Row-identity ledger bumper (L2). Late-bound: the accessor that owns the
   * ledger is attached after this client is built (`LonaSdk.Client`'s constructor, once its rows accessor exists), so
   * `attachLedger` both records it for future lazy preference construction and
   * forwards it to any preference instances already created.
   */
  #ledger: Option<LedgerBumper> = null;

  /** The owning client's liveness, handed to every face: each writer checks it first. */
  readonly #lifecycle: ClientLifecycle;

  /** `lifecycle`: the owning client's (a standalone use: `new LonaSdk.Client.Lifecycle()`). */
  constructor(storage: PreferencesStorage, host: SheetHost, invalidation: InvalidationSink, lifecycle: ClientLifecycle) {
    this.#lifecycle = lifecycle;
    this.#storage = storage;
    this.#host = host;
    this.#invalidation = invalidation;
  }

  /** Late-bind the row-identity ledger to this client and its live prefs. */
  attachLedger(ledger: LedgerBumper): void {
    this.#ledger = ledger;
    this.#calendarPrefs?.attachLedger(ledger);
    this.#timezonePrefs?.attachLedger(ledger);
  }

  calendar(): CalendarPreferences {
    if (!this.#calendarPrefs) {
      this.#calendarPrefs = new CalendarPreferences(this.#storage, this.#invalidation, this.#ledger, this.#lifecycle);
    }
    return this.#calendarPrefs;
  }

  /** The stored view config of the sheet `sheetId`. */
  sheetView(sheetId: SheetId): SheetViewPreferences {
    return new SheetViewPreferences(this.#storage, sheetId, this.#invalidation, this.#lifecycle);
  }

  calendarVisibility(userId: UserId): CalendarVisibilityPreferences {
    return new CalendarVisibilityPreferences(this.#storage, userId);
  }

  rowHeight(): RowHeightPreferences {
    if (!this.#rowHeightPrefs) {
      this.#rowHeightPrefs = new RowHeightPreferences(this.#storage, this.#invalidation, this.#lifecycle);
    }
    return this.#rowHeightPrefs;
  }

  timezone(): TimezonePreferences {
    if (!this.#timezonePrefs) {
      // `setTimezone` announces `TimezoneChanged` itself, stamped with its
      // writer.
      this.#timezonePrefs = new TimezonePreferences(this.#storage, this.#invalidation, this.#ledger, this.#lifecycle);
    }
    return this.#timezonePrefs;
  }

  transitions(): TransitionPreferences {
    if (!this.#transitionsPrefs) {
      this.#transitionsPrefs = new TransitionPreferences(this.#storage, this.#lifecycle);
    }
    return this.#transitionsPrefs;
  }

  sidebarWidth(): SidebarWidthPreferences {
    if (!this.#sidebarWidthPrefs) {
      this.#sidebarWidthPrefs = new SidebarWidthPreferences(this.#storage, this.#host, this.#invalidation, this.#lifecycle);
    }
    return this.#sidebarWidthPrefs;
  }

  /** Per-timescale column counts to use while the sidebar is docked. */
  dockedColumnCount(): DockedColumnCountPreferences {
    if (!this.#dockedColumnCountPrefs) {
      this.#dockedColumnCountPrefs = new DockedColumnCountPreferences(this.#storage, this.#invalidation, this.#lifecycle);
    }
    return this.#dockedColumnCountPrefs;
  }
}
